import PdImage from '@/components/pd-image/pd-image'
import { FC, useCallback, useEffect, useRef } from 'react'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'

gsap.registerPlugin(ScrollTrigger, useGSAP)
interface CountryMarker {
  title: string
  desc: string
  startX: number
  startY: number
  endX: number
  endY: number
}

interface WorldMapProps {
  mediaUrl: string
  locationItems: CountryMarker[]
}

const WorldMap: FC<WorldMapProps> = ({ mediaUrl, locationItems }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<number>(0)
  const progressRef = useRef(0)

  const drawMarkers = useCallback(
    (progress: number) => {
      const canvas = canvasRef.current
      if (!canvas) return

      const ctx = canvas.getContext('2d')
      if (!ctx) return
      // 清除画布
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      // 设置样式
      ctx.strokeStyle = '#06F'
      ctx.fillStyle = '#06F'
      ctx.lineWidth = 1

      locationItems.forEach((locationItem) => {
        const markerProgress = Math.max(0, Math.min(1, progress))
        if (markerProgress <= 0) return

        const startX = (locationItem.startX / 1200) * canvas.width
        const startY = (locationItem.startY / 600) * canvas.height
        const endX = (locationItem.endX / 1200) * canvas.width
        const endY = (locationItem.endY / 600) * canvas.height

        // 绘制连线
        ctx.beginPath()
        ctx.moveTo(startX, startY)

        // 计算当前动画进度下的水平和垂直线段长度
        const totalDistance = Math.abs(endX - startX) + Math.abs(endY - startY)
        const currentDistance = totalDistance * markerProgress

        // 如果动画进度在垂直线段内
        if (currentDistance <= Math.abs(endY - startY)) {
          // 只绘制部分垂直线
          const vProgressY = startY + (endY - startY) * (currentDistance / Math.abs(endY - startY))
          ctx.lineTo(startX, vProgressY)
        }
        // 如果动画进度已经超过垂直线段
        else {
          // 先完整绘制垂直线
          ctx.lineTo(startX, endY)

          // 再绘制部分水平线
          const hProgress = (currentDistance - Math.abs(endY - startY)) / Math.abs(endX - startX)
          const hProgressX = startX + (endX - startX) * hProgress
          ctx.lineTo(hProgressX, endY)
        }
        ctx.stroke()

        // 绘制大圆点
        ctx.beginPath()
        ctx.arc(startX, startY, 10, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(0, 102, 255, 0.10)'
        ctx.fill()

        // 绘制小圆点
        ctx.beginPath()
        ctx.arc(startX, startY, 2.5, 0, Math.PI * 2)
        ctx.fillStyle = '#06F'
        ctx.fill()

        // 绘制文字
        if (markerProgress > 0.8) {
          const textOpacity = (markerProgress - 0.8) * 5
          // 保存当前的填充颜色
          const originalFillStyle = ctx.fillStyle

          // 提取字体名称
          const fontFamily = ctx.font.split(' ').slice(1).join(' ')
          const dpr = window.devicePixelRatio || 1

          // 国家
          ctx.fillStyle = 'rgba(0, 0, 0, 0.90)'
          ctx.globalAlpha = textOpacity
          const textEndX = startX > endX ? endX - 6 * dpr : endX + 6 * dpr
          const textAlign = startX > endX ? 'right' : 'left'
          ctx.font = `600 ${16 * dpr}px ${fontFamily}`
          ctx.textAlign = textAlign
          ctx.fillText(locationItem.title, textEndX, endY + 8 * dpr)

          // 描述
          ctx.fillStyle = 'rgba(0, 0, 0, 0.60)'
          ctx.font = `${12 * dpr}px ${fontFamily}`
          ctx.fillText(locationItem.desc, textEndX, endY + 25 * dpr)
          ctx.globalAlpha = 1
          // 恢复原来的填充颜色（用于绘制其他元素）
          ctx.fillStyle = originalFillStyle
        }
      })
    },
    [locationItems]
  )

  const animate = useCallback(() => {
    if (progressRef.current < 1) {
      progressRef.current += 0.01
      drawMarkers(progressRef.current)
      animationRef.current = requestAnimationFrame(animate)
    }
  }, [drawMarkers])

  useGSAP(() => {
    if (!locationItems.length) return
    const tl = gsap.timeline({
      scrollTrigger: {
        scroller: '#main',
        trigger: containerRef.current,
        start: 'top center',
        end: 'center center',
        once: true,
        onEnter: () => {
          animate()
        },
      },
    })
    return () => {
      tl.kill()
      cancelAnimationFrame(animationRef.current)
    }
  }, [locationItems])

  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current && containerRef.current) {
        const dpr = window.devicePixelRatio || 1
        canvasRef.current.width = containerRef.current.clientWidth * dpr
        canvasRef.current.height = containerRef.current.clientHeight * dpr
        drawMarkers(progressRef.current)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [drawMarkers])

  return (
    <div ref={containerRef} className="w-full h-full relative">
      <PdImage src={mediaUrl} alt="Global Distribution" width="100%" height="100%" object="fill" />
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
    </div>
  )
}

export default WorldMap
